import React, { FC } from 'react';

import { Box, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import PlayLargeIcon from '../../../../assets/icons/PlayLargeIcon';
import { CardImageLabel } from '../../../../components/Card';
import { IKImage } from '../../../../components/IKImage';
import styled from '../../../../styled';
import { SMALL_MARGIN_PX, SUBMIDDLE_MARGIN_PX } from '../../../../theme';
import { DiscoverPathwaysDetailedInfo } from '../../../../types';
import { HITS_TYPES, PATHWAY_STATUSES } from '../../../const';
import type { SendHitsEvent } from '../../const';
import { sendAlgoliaClickEvent } from '../../helpers';
import { getGeneralizedObject } from '../Hit/Hit';
import PathwayStatus from './PathwayStatus';

interface GridHitProps {
  hit?: any;
  sendEvent: SendHitsEvent;
  pathwayProgress?: DiscoverPathwaysDetailedInfo | null;
}

function calcPathwayStatus({ hasUncompletedQuizes, watchedVideos, totalVideos }): PATHWAY_STATUSES {
  const started = !!watchedVideos;
  const notStarted = !started;

  if (notStarted) {
    return PATHWAY_STATUSES.NOT_WATCHED;
  }

  const watched = started && watchedVideos === totalVideos;

  const status = watched
    ? hasUncompletedQuizes
      ? PATHWAY_STATUSES.WATCHED
      : PATHWAY_STATUSES.COMPLETED
    : PATHWAY_STATUSES.STARTED;

  return status;
}

const GridHit: FC<GridHitProps> = ({ hit, sendEvent, pathwayProgress }) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const data = getGeneralizedObject(hit, undefined, true, i18n.language);

  const handleClickHit = () => {
    sendAlgoliaClickEvent(sendEvent, hit);
  };

  const watchedVideos = pathwayProgress?.count_watched_videos || 0;
  const totalVideos = data?.count_videos || 0;

  const status = calcPathwayStatus({
    hasUncompletedQuizes: Boolean(pathwayProgress?.not_passed_quizes?.length),
    watchedVideos,
    totalVideos,
  });

  let statusTooltip;

  switch (status) {
    case PATHWAY_STATUSES.STARTED:
      statusTooltip = `${Math.round((watchedVideos * 100) / totalVideos)} % watched`;
      break;
    case PATHWAY_STATUSES.WATCHED:
      statusTooltip = 'Take a test';
      break;
    case PATHWAY_STATUSES.COMPLETED:
      statusTooltip = 'Completed';
  }

  return (
    <Wrapper>
      {status !== PATHWAY_STATUSES.NOT_WATCHED && (
        <StyledPathwayStatus status={status} tooltip={statusTooltip} />
      )}
      <StyledLink to={data?.link} onClick={handleClickHit}>
        <Image path={data?.preview_url} />
        {data?.searchableType === HITS_TYPES.VIDEO && (
          <PlayIconWrap>
            <PlayIcon color={theme.palette.common.white} />
          </PlayIconWrap>
        )}
        {data?.searchableType === HITS_TYPES.PATHWAY && (
          <StyledCardImageLabel
            name={t('page.discover.pathway')}
            bgColor={theme.palette.common.pink}
            textColor={theme.palette.secondary.dark}
          />
        )}
        {data?.searchableType === HITS_TYPES.NEWS && (
          <StyledCardImageLabel
            name={t('page.discover.news')}
            bgColor={theme.palette.common.blueLight}
          />
        )}
      </StyledLink>
      <InfoWrap>
        <LabelsWrapper>
          {data?.categoriesArr && data?.categoriesArr.length > 0 && (
            <CategoriesWrapper>{data.categoriesArr}</CategoriesWrapper>
          )}
        </LabelsWrapper>
        <Link to={data?.link || ''} onClick={handleClickHit}>
          <Title variant="h2">{data?.title}</Title>
        </Link>
        <Description>{data?.accompanyingText}</Description>
      </InfoWrap>
    </Wrapper>
  );
};

export default GridHit;

const StyledPathwayStatus = styled(PathwayStatus)`
  position: absolute;
  right: 0;
  top: 0;
  z-index: 1;
  padding: 12px;
  box-sizing: content-box;
`;
const StyledCardImageLabel = styled(CardImageLabel)`
  position: absolute;
  top: 12px;
  left: 12px;
`;

const Wrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  height: 338px;
  width: 100%;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: ${SMALL_MARGIN_PX};
  background: ${({ theme }) => theme.palette.secondary.light};
  position: relative;
`;
const StyledLink = styled(Link)`
  width: 100%;
  height: 208px;
  overflow: hidden;
  position: absolute;
  top: 0;
  left: 0;

  &::after {
    content: '';
    display: block;
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 90%;
    background: linear-gradient(
        0deg,
        ${({ theme }) => theme.palette.common.blackSecondary} 0%,
        ${({ theme }) => alpha(theme.palette.common.blackSecondary, 0.9)} 17%,
        ${({ theme }) => alpha(theme.palette.common.blackSecondary, 0)} 100%
      )
      0% 0% no-repeat;
    -webkit-background-origin: padding-box;
    -o-background-origin: padding-box;
    -moz-background-origin: padding-box;
  }

  &:hover > img {
    height: 110%;
  }
`;
const Image = styled(IKImage)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: height 0.3s ease;
`;
const PlayIconWrap = styled(Box)`
  position: absolute;
  width: 64px;
  height: 64px;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 5;
  border-radius: 50%;
  background: ${({ theme }) => alpha(theme.palette.common.blueDark, 0.5)};
`;
const PlayIcon = styled(PlayLargeIcon)`
  width: 28px;
  height: 32px;
  margin-left: 6px;
`;
const InfoWrap = styled(Box)`
  position: relative;
  padding: 0 ${SUBMIDDLE_MARGIN_PX} ${SUBMIDDLE_MARGIN_PX};
`;
const LabelsWrapper = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;

  & .cpdLabel {
    padding: 4.5px 9px;
  }
`;
const CategoriesWrapper = styled(Box)`
  display: inline-flex;
  flex-wrap: wrap;

  & > a:not(:last-child) {
    margin-right: 9px;
    margin-bottom: 9px;
  }

  & > div {
    margin-right: 9px;
    margin-bottom: 9px;

    &:last-child {
      margin-right: 0;
    }
  }

  &:not(:only-child) {
    margin-right: 10px;
  }
`;
const Title = styled(Typography)`
  && {
    font-size: 16px;
    line-height: 1.2;
    font-weight: bold;
    margin-bottom: 9px;
    color: ${({ theme }) => theme.palette.common.white};
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    word-break: break-word;

    ${({ theme }) => theme.breakpoints.up('md')} {
      font-size: 20px;
    }
  }
`;
const Description = styled(Typography)`
  font-size: 14px;
  line-height: 1.2;
  font-weight: normal;
  color: ${({ theme }) => theme.palette.common.violet};
`;
